// "Learning backed by partners you can trust" — heading + subtitle + auto-scrolling
// logo marquee (infinite loop, pause on hover). Logos are CMS-driven (Block List "partners").
import { useEffect, useRef, useState } from "react";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";

const Logo = ({ p }) => {
  const img = <img src={p.logo} alt={p.name || ""} loading="lazy" />;
  return (
    <span className="partners-marquee__item">
      {p.link ? (
        <a href={p.link} target="_blank" rel="noopener noreferrer" aria-label={p.name || "partner"}>{img}</a>
      ) : img}
    </span>
  );
};

// One marquee line. The track is `copies` repetitions of the row, animated
// translateX(0 → -50%). For a seamless infinite loop with no gap on wide /
// zoomed-out screens, ONE half of the track must be at least the container
// width — we grow `copies` until it fits (converges in 2 renders; re-runs on
// resize). Duration scales with set count to keep a constant scroll speed.
function PartnersRow({ row, reverse }) {
  const marqueeRef = useRef(null);
  const trackRef = useRef(null);
  const [copies, setCopies] = useState(2);

  useEffect(() => {
    const marquee = marqueeRef.current;
    const track = trackRef.current;
    if (!marquee || !track || !row.length) return;
    const fit = () => {
      const oneSet = track.scrollWidth / copies; // width of the row once
      if (!oneSet) return;
      const perHalf = Math.max(1, Math.ceil(marquee.clientWidth / oneSet));
      const want = perHalf * 2; // two identical halves -> seamless -50% loop
      setCopies((prev) => (prev === want ? prev : want));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [row.length, copies]);

  return (
    <div
      className={"partners-marquee" + (reverse ? " partners-marquee--reverse" : "")}
      aria-label="Partners"
      ref={marqueeRef}
    >
      <div
        className="partners-marquee__track"
        ref={trackRef}
        style={{ animationDuration: `${(copies / 2) * 40}s` }}
      >
        {Array.from({ length: copies * row.length }).map((_, i) => (
          <Logo key={i} p={row[i % row.length]} />
        ))}
      </div>
    </div>
  );
}

export default function PartnersSection({ props }) {
  const [lang, setLang] = useState(null);
  const [t, setT] = useState({});

  useEffect(() => {
    if (!i18n.isInitialized) initializeI18n().then(() => setLang(i18n.language));
    else setLang(i18n.language);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const out = {};
      out.title = await translate("UMBRACO_LANDING.HOME.PARTNERS.TITLE", false, "Learning backed by partners you can trust");
      out.subtitle = await translate(
        "UMBRACO_LANDING.HOME.PARTNERS.SUBTITLE",
        false,
        "From leading Vietnamese universities to international education institutions, DOTORE collaborates with trusted organizations to deliver a better Korean learning experience."
      );
      if (active) setT(out);
    })();
    return () => { active = false; };
  }, [lang]);

  // i18n drives the text (varies by language). translate() returns the sheet value for the
  // current language, or the English fallback; CMS prop is only a last-resort override.
  const title = t.title || props?.partnersTitle || "";
  const subtitle = t.subtitle || props?.partnersSubtitle || "";
  // Two separately-managed rows (CMS: partnersRow1 / partnersRow2) so each line of logos
  // is edited independently. Each row scrolls (row 2 in reverse) and is repeated so the
  // marquee loops seamlessly.
  const r1 = (props?.partnersRow1 || []).filter((p) => p && p.logo);
  const r2 = (props?.partnersRow2 || []).filter((p) => p && p.logo);
  let rows = [r1, r2].filter((r) => r.length);

  // Backward-compat: a single "partners" list is auto-split into two rows.
  if (!rows.length) {
    const all = (props?.partners || []).filter((p) => p && p.logo);
    const half = Math.ceil(all.length / 2);
    rows = [all.slice(0, half), all.slice(half)].filter((r) => r.length);
  }

  // Nothing configured in CMS yet -> render nothing (no empty bar).
  if (!rows.length) return null;

  return (
    <section className="partners-section">
      <div className="partners-section__head">
        <h2 className="partners-section__title animate-on-scroll">{title}</h2>
        {subtitle && <p className="partners-section__subtitle animate-on-scroll">{subtitle}</p>}
      </div>

      {rows.map((row, ri) => (
        <PartnersRow key={ri} row={row} reverse={ri === 1} />
      ))}
    </section>
  );
}
